import React, { createContext, useContext, useState, useEffect } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';

// Theme Context
const ThemeContext = createContext();

// Theme values
const THEMES = {
  light: {
    name: 'light',
    bg: '#FFFFFF',
    bgSecondary: '#F8FAFC',
    bgCard: '#FFFFFF',
    text: '#1E293B',
    textSecondary: '#64748B',
    border: '#E2E8F0',
    accent: '#3a7189'
  },
  dark: {
    name: 'dark',
    bg: '#0d1218',
    bgSecondary: '#1c262d',
    bgCard: '#1c262d',
    text: '#FFFFFF',
    textSecondary: '#93a5af',
    border: 'rgba(255,255,255,0.1)',
    accent: '#3a7189'
  },
  system: {
    name: 'system'
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark';
    }
    return 'dark';
  });

  const [resolvedTheme, setResolvedTheme] = useState('dark');

  useEffect(() => {
    // Handle system preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const updateResolvedTheme = () => {
      if (theme === 'system') {
        setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
      } else {
        setResolvedTheme(theme);
      }
    };

    updateResolvedTheme();
    mediaQuery.addEventListener('change', updateResolvedTheme);

    return () => mediaQuery.removeEventListener('change', updateResolvedTheme);
  }, [theme]);

  useEffect(() => {
    // Apply theme to document
    const root = document.documentElement;
    const themeValues = THEMES[resolvedTheme] || THEMES.dark;

    root.style.setProperty('--bg-primary', themeValues.bg);
    root.style.setProperty('--bg-secondary', themeValues.bgSecondary);
    root.style.setProperty('--bg-card', themeValues.bgCard);
    root.style.setProperty('--text-primary', themeValues.text);
    root.style.setProperty('--text-secondary', themeValues.textSecondary);
    root.style.setProperty('--border-color', themeValues.border);
    root.style.setProperty('--accent-color', themeValues.accent);

    // Set class on body
    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(`${resolvedTheme}-theme`);

    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme, resolvedTheme]);

  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'dark') return 'light';
      if (prev === 'light') return 'system';
      return 'dark';
    });
  };

  const value = {
    theme,
    resolvedTheme,
    setTheme,
    toggleTheme,
    isDark: resolvedTheme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

/**
 * Theme Toggle Button Component
 */
export function ThemeToggle({ className = '' }) {
  const { theme, toggleTheme } = useTheme();

  const getIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="w-5 h-5" />;
      case 'dark':
        return <Moon className="w-5 h-5" />;
      default:
        return <Monitor className="w-5 h-5" />;
    }
  };

  const getLabel = () => {
    switch (theme) {
      case 'light':
        return 'Modo claro';
      case 'dark':
        return 'Modo escuro';
      default:
        return 'Automático';
    }
  };

  return (
    <button
      onClick={toggleTheme}
      className={`flex items-center gap-2 p-2 rounded-lg transition-colors hover:bg-white/10 ${className}`}
      title={getLabel()}
      data-testid="theme-toggle"
    >
      {getIcon()}
      <span className="text-sm hidden sm:inline">{getLabel()}</span>
    </button>
  );
}

/**
 * Theme Selector Dropdown
 */
export function ThemeSelector({ className = '' }) {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);

  const options = [
    { value: 'light', label: 'Claro', icon: <Sun className="w-4 h-4" /> },
    { value: 'dark', label: 'Escuro', icon: <Moon className="w-4 h-4" /> },
    { value: 'system', label: 'Sistema', icon: <Monitor className="w-4 h-4" /> }
  ];

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
      >
        {options.find(o => o.value === theme)?.icon}
        <span className="text-sm">{options.find(o => o.value === theme)?.label}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 bg-[#1c262d] border border-white/10 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setTheme(option.value);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/10 transition-colors ${
                  theme === option.value ? 'text-[#3a7189] bg-[#3a7189]/10' : 'text-white'
                }`}
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ThemeProvider;
